class CartDrawer extends HTMLElement {
  constructor() {
    super();
    this.addEventListener('keyup', (evt) => evt.code === 'Escape' && this.close());
    this.handleOutsideClick = this.handleOutsideClick.bind(this);
    this.addEventListener('click', this.handleOutsideClick);
    this.setHeaderCartIconAccessibility();
  }

  handleOutsideClick(event) {
    if (!(event.target instanceof Element) || event.target.closest('.drawer__inner')) return;
    this.close();
  }

  setHeaderCartIconAccessibility() {
    const cartLink = document.querySelector('#cart-icon-bubble');
    if (!cartLink) return;

    cartLink.setAttribute('role', 'button');
    cartLink.setAttribute('aria-haspopup', 'dialog');
    cartLink.addEventListener('click', (event) => {
      event.preventDefault();
      this.open(cartLink);
    });

    cartLink.addEventListener('keydown', (event) => {
      if (event.code.toUpperCase() === 'SPACE') {
        event.preventDefault();
        this.open(cartLink);
      }
    });
  }

  open(triggeredBy) {
    if (triggeredBy) this.setActiveElement(triggeredBy);

    const cartDrawerNote = this.querySelector('[id^="Details-"] summary');
    if (cartDrawerNote && !cartDrawerNote.hasAttribute('role'))
      this.setSummaryAccessibility(cartDrawerNote);

    setTimeout(() => {
      this.classList.add('animate', 'active');
    });

    this.addEventListener('transitionend', () => {
      const containerToTrapFocusOn = this.classList.contains('is-empty')
        ? this.querySelector('.drawer__inner-empty')
        : document.getElementById('CartDrawer');
      const focusElement = this.querySelector('.drawer__inner') || this.querySelector('.drawer__close');
      trapFocus(containerToTrapFocusOn, focusElement);
    }, { once: true });

    document.body.classList.add('overflow-hidden');
  }

  close() {
    this.classList.remove('active');
    removeTrapFocus(this.activeElement);
    document.body.classList.remove('overflow-hidden');
  }

  setSummaryAccessibility(cartDrawerNote) {
    cartDrawerNote.setAttribute('role', 'button');
    cartDrawerNote.setAttribute('aria-expanded', 'false');

    if (cartDrawerNote.nextElementSibling.getAttribute('id')) {
      cartDrawerNote.setAttribute('aria-controls', cartDrawerNote.nextElementSibling.id);
    }

    cartDrawerNote.addEventListener('click', (event) => {
      event.currentTarget.setAttribute('aria-expanded', !event.currentTarget.closest('details').hasAttribute('open'));
    });

    cartDrawerNote.parentElement.addEventListener('keyup', onKeyUpEscape);
  }

  renderContents(parsedState) {
    const innerDrawer = this.querySelector('.drawer__inner');
    if (innerDrawer?.classList.contains('is-empty')) {
      innerDrawer.classList.remove('is-empty');
    }

    this.productId = parsedState.id;

    this.getSectionsToRender().forEach((section) => {
      const sectionElement = section.selector
        ? document.querySelector(section.selector)
        : document.getElementById(section.id);

      if (!sectionElement) return;

      const html = this.getSectionInnerHTML(parsedState.sections[section.id], section.selector);
      if (html) sectionElement.innerHTML = html;
    });

    setTimeout(() => {
      this.open();
    });
  }

  getSectionInnerHTML(html, selector = '.shopify-section') {
    const doc = new DOMParser().parseFromString(html, 'text/html');
    return doc.querySelector(selector)?.innerHTML;
  }

  getSectionsToRender() {
    return [
      { id: 'cart-drawer', selector: '#CartDrawer' },
      { id: 'cart-icon-bubble' },
    ];
  }

  setActiveElement(element) {
    this.activeElement = element;
  }
}

customElements.define('cart-drawer', CartDrawer);

class CartDrawerItems extends CartItems {
  getSectionsToRender() {
    return [
      { id: 'CartDrawer', section: 'cart-drawer', selector: '.drawer__inner' },
      { id: 'cart-icon-bubble', section: 'cart-icon-bubble', selector: '.shopify-section' },
    ];
  }
}

customElements.define('cart-drawer-items', CartDrawerItems);

function updateCartItemCounts(count) {
  document.querySelectorAll(".cart-count").forEach((el) => {
    el.textContent = count;
  });
}

async function updateCartDrawer() {
  try {
    const cartRes = await fetch("/cart.js");
    const cartData = await cartRes.json();
    updateCartItemCounts(cartData.item_count);

    const reqPrefix = Shopify.locale === 'en' ? '' : `/${Shopify.locale}` 

    const sectionRes = await fetch(`${reqPrefix}/?sections=cart-drawer`);
    if (!sectionRes.ok) throw new Error("Failed to load cart-drawer section");

    const sectionData = await sectionRes.json();
    const drawerHTML = sectionData["cart-drawer"];

    const parser = new DOMParser();
    const doc = parser.parseFromString(drawerHTML, "text/html");
    const newDrawer = doc.querySelector("cart-drawer");
    const existingDrawer = document.querySelector("cart-drawer");

    if (newDrawer && existingDrawer) {
      existingDrawer.innerHTML = newDrawer.innerHTML;
      existingDrawer.classList.remove("is-empty");
      addCartDrawerListeners();
    }
  } catch (err) {
    console.error("Cart drawer update failed:", err);
  }
}

function addCartDrawerListeners() {
  document
    .querySelectorAll(".cart-drawer-quantity-selector button")
    .forEach((button) => {
      button.addEventListener("click", async () => {
        try {
          const rootItem = button.closest("[data-line-item-key]");
          const key = rootItem.getAttribute("data-line-item-key");
          const currentQuantity = Number(button.parentElement.querySelector("input").value);
          const isUp = button.classList.contains("cart-drawer-quantity-selector-plus");
          const newQuantity = isUp ? currentQuantity + 1 : currentQuantity - 1;

          const res = await fetch("/cart/update.js", {
            method: "POST",
            headers: { Accept: "application/json", "Content-Type": "application/json" },
            body: JSON.stringify({ updates: { [key]: newQuantity } }),
          });

          await res.json();
          await updateCartDrawer();
        } catch (err) {
          console.error("Failed to update item quantity:", err);
        }
      });
    });

  document.querySelector(".cart-drawer-box")?.addEventListener("click", (e) => {
    e.stopPropagation();
  });

  document
    .querySelectorAll(".cart-drawer-header-right-close")
    .forEach((el) => {
      el.addEventListener("click", () => {
        closeCartDrawer();
      });
    });
}

function openCartDrawer() {
  document.querySelector(".cart-drawer")?.classList.add("cart-drawer--active");
}

function closeCartDrawer() {
  document.querySelector(".cart-drawer")?.classList.remove("cart-drawer--active");
}

document.addEventListener("DOMContentLoaded", () => {
  addCartDrawerListeners();

  document.querySelectorAll('form[action*="/cart/add"]').forEach((form) => {
    form.addEventListener("submit", async (e) => {
      e.preventDefault();
      const formData = new FormData(form);

      try {
        const res = await fetch("/cart/add.js", {
          method: "POST",
          body: formData,
          headers: {
            Accept: "application/json"
          }
        });

        if (!res.ok) throw new Error("Failed to add item to cart");

        await new Promise((resolve) => setTimeout(resolve, 150));

        await updateCartDrawer();
      } catch (err) {
        console.error("Add to cart failed:", err);
      }
    });
  });

  document.querySelectorAll('a[href="/cart"]').forEach((a) => {
    a.addEventListener("click", (e) => {
      e.preventDefault();
      openCartDrawer();
    });
  });
});
